import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import settingsService from '../services/settingsService';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog"
import { Button } from "../components/ui/button"

import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Cloud,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ArrowRight,
  UserPlus,
  Clock,
  ShieldCheck,
} from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ username: '', email: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [registrationPending, setRegistrationPending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [appSettings, setAppSettings] = useState(null);

  useEffect(() => {
    settingsService.getSettings().then(setAppSettings).catch(err => console.error('Failed to load settings:', err));
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const getPasswordStrength = () => {
    const p = formData.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 6) s += 25;
    if (p.length >= 8) s += 25;
    if (p.match(/[a-z]+/)) s += 25;
    if (p.match(/[A-Z]+/) || p.match(/[0-9]+/)) s += 25;
    return s;
  };

  const strengthMeta = () => {
    const s = getPasswordStrength();
    if (s === 0) return { label: '', color: '#e2e8f0' };
    if (s <= 25) return { label: 'Lemah', color: '#ef4444' };
    if (s <= 50) return { label: 'Sedang', color: '#f59e0b' };
    if (s <= 75) return { label: 'Kuat', color: '#3b82f6' };
    return { label: 'Sangat Kuat', color: '#10b981' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.username || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Semua field harus diisi'); return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Password tidak cocok'); return;
    }
    if (formData.password.length < 6) {
      setError('Password minimal 6 karakter'); return;
    }
    setLoading(true);
    try {
      const response = await authService.register(formData.username, formData.email, formData.password);
      if (response.pending) {
        setRegistrationPending(true);
        setPendingEmail(formData.email);
        setLoading(false);
      } else {
        setLoading(false);
        navigate('/login', { state: { message: 'Registrasi berhasil! Silakan login.' } });
      }
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Registrasi gagal. Silakan coba lagi.');
    }
  };

  // =========================================================
  // PENDING APPROVAL VIEW
  // =========================================================
  if (registrationPending) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
        {/* Left panel */}
        <LeftPanel appSettings={appSettings} logoError={logoError} setLogoError={setLogoError} />

        {/* Right panel - success state */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', padding: '40px 24px', overflowY: 'auto' }}>
          <div style={{ width: '100%', maxWidth: '420px', textAlign: 'center' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: '0 4px 20px rgba(16,185,129,0.2)' }}>
              <CheckCircle2 size={38} color="#16a34a" />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Registrasi Berhasil!</h2>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '28px' }}>
              Akun Anda dengan email <strong style={{ color: '#0f172a' }}>{pendingEmail}</strong> sedang menunggu persetujuan administrator.
            </p>

            <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '20px', textAlign: 'left', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
              <p style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a', marginBottom: '14px' }}>Apa selanjutnya?</p>
              {[
                { icon: Clock, text: 'Administrator akan meninjau registrasi Anda dalam 1–2 hari kerja' },
                { icon: Mail, text: 'Anda akan menerima notifikasi email setelah akun disetujui' },
                { icon: ShieldCheck, text: 'Setelah disetujui, login menggunakan email dan password Anda' },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: i < 2 ? '12px' : 0 }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                    <Icon size={14} color="#3b82f6" />
                  </div>
                  <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>{text}</p>
                </div>
              ))}
            </div>

            <div style={{ background: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', padding: '12px 16px', marginBottom: '24px', fontSize: '12px', color: '#1d4ed8' }}>
              <strong>Catatan:</strong> Anda tidak dapat login sampai akun disetujui oleh administrator.
            </div>

            <button
              onClick={() => navigate('/login')}
              style={{ width: '100%', height: '48px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: '#fff', fontSize: '15px', fontWeight: 700, boxShadow: '0 4px 16px rgba(99,102,241,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              Kembali ke Halaman Login <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // MAIN REGISTER FORM — SPLIT SCREEN
  // =========================================================
  const { label: strengthLabel, color: strengthColor } = strengthMeta();

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* ===== LEFT PANEL ===== */}
      <LeftPanel appSettings={appSettings} logoError={logoError} setLogoError={setLogoError} />

      {/* ===== RIGHT PANEL (register form) ===== */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', padding: '40px 24px', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>

          {/* Header */}
          <div style={{ marginBottom: '28px' }}>
            <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
              Buat Akun Baru
            </h1>
            <p style={{ color: '#64748b', fontSize: '14px' }}>
              Daftarkan diri untuk menggunakan layanan penyimpanan
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fca5a5', marginBottom: '20px' }}>
              <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0 }} />
              <span style={{ color: '#dc2626', fontSize: '13px' }}>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Username */}
            <div>
              <label style={labelStyle}>Username <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <User size={16} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  name="username"
                  type="text"
                  placeholder="Masukkan username Anda"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={loading}
                  style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label style={labelStyle}>Alamat Email <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <Mail size={16} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  name="email"
                  type="email"
                  placeholder="email.anda@contoh.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label style={labelStyle}>Kata Sandi <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Minimal 6 karakter"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ ...inputStyle, paddingRight: '44px' }}
                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={eyeBtnStyle}>
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {/* Password strength */}
              {formData.password && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {[25, 50, 75, 100].map(threshold => (
                      <div key={threshold} style={{ flex: 1, height: '3px', borderRadius: '999px', background: getPasswordStrength() >= threshold ? strengthColor : '#e2e8f0', transition: 'background 0.3s ease' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: '11px', color: strengthColor, fontWeight: 600 }}>{strengthLabel}</span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label style={labelStyle}>Konfirmasi Kata Sandi <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Masukkan ulang kata sandi"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  style={{
                    ...inputStyle,
                    paddingRight: '44px',
                    borderColor: formData.confirmPassword && formData.password !== formData.confirmPassword ? '#ef4444' : '#e2e8f0',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                  onBlur={e => {
                    e.target.style.boxShadow = 'none';
                    e.target.style.borderColor = formData.confirmPassword && formData.password !== formData.confirmPassword ? '#ef4444' : '#e2e8f0';
                  }}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={eyeBtnStyle}>
                  {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>Password tidak sama</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', height: '48px', borderRadius: '12px', border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                color: '#fff', fontSize: '15px', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                boxShadow: loading ? 'none' : '0 4px 16px rgba(99,102,241,0.4)',
                transition: 'all 0.2s ease',
                marginTop: '4px',
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(99,102,241,0.5)'; } }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = loading ? 'none' : '0 4px 16px rgba(99,102,241,0.4)'; }}
            >
              {loading ? (
                <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Mendaftar...</>
              ) : (
                <><UserPlus size={16} /> Daftar Sekarang</>
              )}
            </button>
          </form>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
            <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
            <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 500 }}>ATAU</span>
            <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
          </div>

          {/* Login link */}
          <div style={{ textAlign: 'center' }}>
            <span style={{ color: '#64748b', fontSize: '14px' }}>Sudah punya akun? </span>
            <button
              onClick={() => navigate('/login')}
              style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '14px', fontWeight: 700, cursor: 'pointer', padding: 0 }}
            >
              Masuk sekarang
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

// =========================================================
// LEFT PANEL — Shared with Login page style
// =========================================================
const LeftPanel = ({ appSettings, logoError, setLogoError }) => (
  <div style={{
    width: '42%',
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 60%, #0f2744 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '48px 40px',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
  }}>
    {/* Decorative circles */}
    <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(56,189,248,0.06)', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(99,102,241,0.08)', pointerEvents: 'none' }} />

    <div>
      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '56px' }}>
        {!logoError && appSettings?.logo_url ? (
          <img src={appSettings.logo_url} alt="Logo" onError={() => setLogoError(true)} style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
        ) : (
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, #38bdf8, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Cloud size={22} color="#fff" />
          </div>
        )}
        <div>
          <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>
            {appSettings?.app_name || 'Data Storage'}
          </div>
          <div style={{ color: '#38bdf8', fontWeight: 600, fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {appSettings?.app_subtitle || 'PDTI-UMI'}
          </div>
        </div>
      </div>

      <h2 style={{ color: '#f1f5f9', fontSize: '28px', fontWeight: 800, lineHeight: 1.25, marginBottom: '16px' }}>
        Bergabung dan mulai<br />simpan data Anda
      </h2>
      <p style={{ color: '#cbd5e1', fontSize: '14px', lineHeight: 1.7, marginBottom: '40px' }}>
        Buat akun untuk mendapatkan akses ke layanan penyimpanan data terpusat yang aman dan terkelola.
      </p>

      {/* Steps */}
      {[
        { step: '1', label: 'Isi formulir pendaftaran', desc: 'Lengkapi data username, email, dan password' },
        { step: '2', label: 'Tunggu persetujuan admin', desc: 'Akun ditinjau dalam 1–2 hari kerja' },
        { step: '3', label: 'Login dan mulai bekerja', desc: 'Akses penyimpanan personal Anda' },
      ].map(({ step, label, desc }) => (
        <div key={step} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '20px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(56,189,248,0.2), rgba(99,102,241,0.15))', border: '1px solid rgba(56,189,248,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#38bdf8', fontWeight: 700, fontSize: '13px' }}>
            {step}
          </div>
          <div>
            <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>{label}</div>
            <div style={{ color: '#94a3b8', fontSize: '12px', lineHeight: 1.5 }}>{desc}</div>
          </div>
        </div>
      ))}
    </div>

    <div style={{ color: '#64748b', fontSize: '11px' }}>
      © 2025 Dibuat oleh <span style={{ color: '#94a3b8', fontWeight: 600 }}>Muhammad Akbar Abdullah</span>
    </div>
  </div>
);

// =========================================================
// Shared style objects
// =========================================================
const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  height: '46px',
  borderRadius: '10px',
  border: '1.5px solid #e2e8f0',
  background: '#fff',
  padding: '0 14px 0 40px',
  fontSize: '14px',
  color: '#0f172a',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
};

const eyeBtnStyle = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#94a3b8',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
};

export default Register;